// react
import React from "react";
import {Button, Form} from "react-bootstrap";
import moment from 'moment';

// local
import {clients, CompanyDetails, NameValuePair, system, UTC_DATE_FORMAT} from "../types";
import {PopupMessage} from "../components";

/**
 * Info
 */

interface InfoProps {
	clientId: number,
	action: string,
	close: () => void,
}
interface InfoState {
	loaded: boolean,
	edit: boolean,
	company: CompanyDetails | null,
	showSaveMessage: string | null
}

export class ClientDetailsForm extends React.Component<InfoProps, InfoState> {

	constructor(props: InfoProps) {
		super(props);

		this.state = {
			loaded: false,
			edit: (props.action === 'edit'),
			company: null,
			showSaveMessage: null
		}

		// Register the callbacks.
		this.onDismissPopup = this.onDismissPopup.bind(this);
		this.onFormClose = this.onFormClose.bind(this);
		this.onFormSubmit = this.onFormSubmit.bind(this);
	}

	componentDidMount = async () => {
		// setup
		const {clientId} = this.props;
		const {edit} = this.state;

		// Get the client details.
		if (edit) {
			const company = await clients.fetchClientInfo(clientId);
			this.setState({loaded: true, company});
		} else {
			const parent = clients.findByName(system.rootId);
			const company = {
				// basic
				id: 0,
				name: "New Client/Location",
				shortname: "",
				parentid: parent ? parent.id : 0,
				code: "",
				logo: "",
				// address
				address: "",
				city: "",
				postcode: "",
				country: "",
				locale: "",
				// other
				census: 0,
				validfrom: 0,
				validto: 0,
				departments: [],
				// settings
				btnColor: "",
				talkNumber: "",
				textNumber: ""
			}
			this.setState({loaded: true, company});
		}
	}

	dateForPicker (dateString: string) {
		return moment(new Date(dateString)).format(UTC_DATE_FORMAT);
	}

	onDismissPopup() {
		this.setState({showSaveMessage: null});
	}

	onFormClose = async (event: any) => {
		// setup
		this.props.close();
	}

	onFormSubmit = async (event: any) => {
		// setup
		event.preventDefault(); // prevent page from reloading
		const {company} = this.state;
		if (!company) {
			return;
		}
		const form = event.currentTarget;

		// Check for changes.
		let changes: NameValuePair[] = [];
		Object.entries(company).forEach(entry => {
			// setup
			const [key, value] = entry;
			let field = form[key];
			if (field) {
				// Determine the value.
				let srcValue;
				const type = field.getAttribute('type');
				if (key === 'census' || key === 'office_census' || key === 'other_census') {
					srcValue = field.value ? parseInt(field.value) : 0;
				} else if (type === 'date') {
					const date = moment.utc(field.value, UTC_DATE_FORMAT);
					srcValue = date.valueOf() / 1000;
				} else if (key === 'talkNumber' || key === 'textNumber') {
					const newValue = field.value.replace('+1').replace(/\D/g,'');
					srcValue  = newValue.replace(/(\d{3})(\d{3})(\d{4})/, '+1-$1-$2-$3');
				} else if (type === 'color') {
					srcValue = field.value.toUpperCase();
				} else {
					srcValue = field.value;
				}

				// See if there is a difference.
				if (srcValue && srcValue != value) {
					changes.push({name: key, value: srcValue});
				}
			}
		});

		// Update the back-end.
		if (!changes.length) {
			this.setState({showSaveMessage: `There is no information to be updated.`, edit: true});
			return;
		}
		const success = await clients.updateClientInfo(company.id, changes);
		if (success) {
			this.setState({showSaveMessage: `${company.name}'s information has been updated.`, edit: true});
		} else if (!success) {
			this.setState({showSaveMessage: `There was a problem updating ${company.name}'s information.`, edit: true});
		}
	}

	uploadFileHandler() {

	}

	render() {
		// setup
		const {edit} = this.state;
		const {loaded, company, showSaveMessage} = this.state;
		if (!loaded || !company) {
			return <></>
		}

		// render
		const canUpdate = true;
		return (
			<div id="ClientForm">
				<Form className="form-popup" onSubmit={this.onFormSubmit}>

					{this.renderFormHeader(company)}
					{this.renderFormBody(company, edit)}
					{this.renderFormFooter(canUpdate, edit)}

				</Form>

				<PopupMessage
					show={!!showSaveMessage}
					title={`${company.name} Information`}
					body={showSaveMessage}
					onClose={this.onDismissPopup}
				/>

			</div>
		);
	}

	renderFormBody(company: CompanyDetails, edit: boolean) {
		// setup
		const startDate = company.validfrom ? moment.utc(company.validfrom * 1000) : moment().utc();
		const strStartDate = startDate.format(UTC_DATE_FORMAT);
		const endDate = company.validto ? moment.utc(company.validto * 1000) : moment().utc();
		const strEndDate = endDate.format(UTC_DATE_FORMAT);

		const body = document.getElementById('ReportsPage-body');
		const style = {"maxHeight": body ? body.clientHeight - 160 : 600};

		// render
		return (
			<div className="form-body scroll-y" style={style}>
			</div>
		)
	}

	renderFormFooter(canUpdate: boolean, edit: boolean) {
		return (
			<div className="form-footer">
				<Button className="btn form-btn" variant="forgot" size="sm" onClick={this.onFormClose}>
					Close
				</Button>
				<Button type="submit" className="btn form-btn" size="sm">
					Update
				</Button>
			</div>
		)
	}

	renderFormHeader(company: CompanyDetails) {
		return (
			<div className="form-header">
				{company.name + ' Client Details'}
				<Button id='ClosePopup' variant="forgot" className="btn form-btn" size="sm" onClick={this.onFormClose}>
					X
				</Button>
			</div>
		)
	}

}

